import React from 'react';
import { Dropdown, Form } from 'react-bootstrap';

export interface CustomerFiltersState {
  name?: string;
  source_type?: string;
  has_conflicts?: string;
  missing_sling_locations?: string;
}

interface CustomerFilterBarProps {
  filters: CustomerFiltersState;
  onFiltersChange: (filters: CustomerFiltersState) => void;
  onClearFilters: () => void;
}

interface TextFilterProps {
  field: keyof CustomerFiltersState;
  label: string;
  icon: string;
  placeholder: string;
  value?: string;
  onChange: (field: keyof CustomerFiltersState, value: string | undefined) => void;
  isClearing?: boolean;
}

const TextFilter: React.FC<TextFilterProps> = ({ field, label, icon, placeholder, value, onChange, isClearing }) => {
  const [tempValue, setTempValue] = React.useState(value || '');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Update temp value when value prop changes
  React.useEffect(() => {
    setTempValue(value || '');
    // Clear any pending debounced update when value is cleared externally
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [value]);

  // Clean up debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleApply = () => {
    // Don't apply if we're in the middle of clearing all filters
    if (isClearing) {
      return;
    }
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Only call onChange if value actually changed
    const newValue = tempValue || undefined;
    if (newValue !== value) {
      onChange(field, newValue);
    }
  };

  const handleInputChange = (newValue: string) => {
    setTempValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced update
    debounceTimerRef.current = setTimeout(() => {
      onChange(field, newValue || undefined);
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
      // Close the dropdown by clicking the toggle button
      const toggleButton = document.getElementById(`dropdown-${field}`);
      if (toggleButton) {
        toggleButton.click();
      }
    }
  };

  return (
    <Dropdown onToggle={(isOpen) => {
      if (!isOpen) {
        // Apply changes when dropdown closes
        handleApply();
      } else {
        // Focus input when dropdown opens
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }}>
      <Dropdown.Toggle
        variant={value ? "primary" : "outline-secondary"}
        size="sm"
        id={`dropdown-${field}`}
        className={value ? "d-flex align-items-center" : ""}
      >
        {value && (
          <>
            <i
              className="bi bi-x-circle me-2"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(field, undefined);
              }}
            ></i>
            <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
          </>
        )}
        <i className={`bi ${icon} me-1`}></i>
        {label}{value && `: ${value}`}
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-header-control" data-bs-theme="light">
        <div className="px-3 py-2" style={{ minWidth: '250px' }} onClick={(e) => e.stopPropagation()}>
          <Form.Control
            ref={inputRef}
            size="sm"
            type="text"
            placeholder={placeholder}
            value={tempValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface SelectFilterProps {
  field: keyof CustomerFiltersState;
  label: string;
  icon: string;
  value?: string;
  options: { value: string; label: string }[];
  onChange: (field: keyof CustomerFiltersState, value: string | undefined) => void;
}

const SelectFilter: React.FC<SelectFilterProps> = ({ field, label, icon, value, options, onChange }) => (
  <Dropdown as="div" className="d-inline-block">
    <Dropdown.Toggle
      variant={value ? "primary" : "outline-secondary"}
      size="sm"
      id={`dropdown-${field}`}
      className={value ? "d-flex align-items-center" : ""}
    >
      {value && (
        <>
          <i
            className="bi bi-x-circle me-2"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onChange(field, undefined);
            }}
          ></i>
          <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
        </>
      )}
      <i className={`bi ${icon} me-1`}></i>
      {label}{value && `: ${options.find(opt => opt.value === value)?.label || value}`}
    </Dropdown.Toggle>
    <Dropdown.Menu className="bg-header-control" data-bs-theme="light">
      <Dropdown.Item onClick={() => onChange(field, undefined)}>
        All
      </Dropdown.Item>
      <Dropdown.Divider />
      {options.map((option) => (
        <Dropdown.Item key={option.value} onClick={() => onChange(field, option.value)}>
          {option.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

interface CheckboxFilterProps {
  field: keyof CustomerFiltersState;
  label: string;
  icon: string;
  value?: string;
  onChange: (field: keyof CustomerFiltersState, value: string | undefined) => void;
}

const CheckboxFilter: React.FC<CheckboxFilterProps> = ({ field, label, icon, value, onChange }) => {
  const isActive = value === 'true';

  return (
    <button
      className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
      onClick={() => onChange(field, isActive ? undefined : 'true')}
    >
      {isActive && (
        <>
          <i
            className="bi bi-x-circle me-2"
            onClick={(e) => {
              e.stopPropagation();
              onChange(field, undefined);
            }}
          ></i>
          <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
        </>
      )}
      <i className={`bi ${icon} me-1`}></i>
      {label}
    </button>
  );
};

const CustomerFilterBar: React.FC<CustomerFilterBarProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const [isClearing, setIsClearing] = React.useState(false);

  const handleFilterChange = (field: keyof CustomerFiltersState, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  // Count active filters
  const activeFilterCount = Object.entries(filters).filter(([, value]) => value !== undefined).length;
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="ms-1 bg-header-control border-bottom p-1 me-1 rounded">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <TextFilter
          field="name"
          label="Name"
          icon="bi-person"
          placeholder="Filter by customer name..."
          value={filters.name}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <SelectFilter
          field="source_type"
          label="Source Type"
          icon="bi-diagram-3"
          value={filters.source_type}
          options={[
            { value: 'internal', label: 'Internal' },
            { value: 'quickbooks', label: 'QuickBooks' },
            { value: 'hubspot', label: 'HubSpot' },
          ]}
          onChange={handleFilterChange}
        />

        <CheckboxFilter
          field="has_conflicts"
          label="Has Conflicts"
          icon="bi-exclamation-triangle"
          value={filters.has_conflicts}
          onChange={handleFilterChange}
        />

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            className="btn btn-outline-danger btn-sm ms-auto"
            onClick={() => {
              setIsClearing(true);
              onClearFilters();
              setTimeout(() => setIsClearing(false), 0);
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomerFilterBar;
